import { DatePicker } from '@mui/x-date-pickers';
import * as React from 'react';
import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import ElementDescription from './ElementDescription';
import { DateTime } from 'luxon';
import { BaseDateRangePickerProps } from '../types/global';
import { checkFeatureFieldEnabled } from '../helpers/configuration';

const INVALID_DATE_RANGE = '__INVALID_DATE_RANGE__';

const BaseDateRangePicker: React.FC<BaseDateRangePickerProps> = ({
  value,
  name,
  onChange,
  errors = null,
  feature = null,
  description = null,
}) => {
  const [fromDate, setFromDate] = React.useState<DateTime | null>(null);
  const [toDate, setToDate] = React.useState<DateTime | null>(null);

  React.useEffect(() => {
    if (!value) {
      setFromDate(null);
      setToDate(null);
      return;
    }

    if (value === INVALID_DATE_RANGE) {
      return;
    }

    const [fromStr = '', toStr = ''] = value.split('-', 2);
    const from = fromStr ? DateTime.fromFormat(fromStr, 'yyyyLLdd') : null;
    const to = toStr ? DateTime.fromFormat(toStr, 'yyyyLLdd') : null;

    setFromDate(from && from.isValid ? from : null);
    setToDate(to && to.isValid ? to : null);
  }, [value]);

  const emitInvalid = () => {
    onChange({ target: { name, value: INVALID_DATE_RANGE } });
  };

  const handleChange = (newFrom: DateTime | null, newTo: DateTime | null) => {
    if (newFrom && !newFrom.isValid) {
      emitInvalid();
      return;
    }

    if (newTo && !newTo.isValid) {
      emitInvalid();
      return;
    }

    const fromStr = newFrom?.isValid ? newFrom.toFormat('yyyyLLdd') : '';
    const toStr = newTo?.isValid ? newTo.toFormat('yyyyLLdd') : '';

    if (!fromStr && !toStr) {
      onChange({ target: { name, value: '' } });
      return;
    }

    onChange({ target: { name, value: `${fromStr}-${toStr}` } });
  };

  const handleFromChange = (newFrom: DateTime | null) => {
    setFromDate(newFrom);
    const hasValidFrom = newFrom && newFrom.isValid;
    const validTo = toDate && hasValidFrom && toDate < newFrom ? null : toDate;
    setToDate(validTo);
    handleChange(newFrom, validTo);
  };

  const handleToChange = (newTo: DateTime | null) => {
    setToDate(newTo);
    handleChange(fromDate, newTo);
  };

  if (name && feature && !checkFeatureFieldEnabled(feature, name)) {
    return null;
  }

  return (
    <FormControl fullWidth>
      <Box display='flex' alignItems='center' gap={1}>
        <DatePicker
          label='Date range From'
          value={fromDate}
          format='d/M/yyyy'
          readOnly={false}
          onChange={handleFromChange}
          slotProps={{
            textField: {
              fullWidth: true,
              inputProps: {
                'data-testid': 'date-from',
              },
            },
          }}
        />
        <Typography variant='h6' sx={{ px: 1 }}>
          –
        </Typography>
        <DatePicker
          label='To'
          value={toDate}
          minDate={fromDate || undefined}
          readOnly={false}
          format='d/M/yyyy'
          onChange={handleToChange}
          slotProps={{
            textField: {
              fullWidth: true,
              inputProps: {
                'data-testid': 'date-to',
              },
            },
          }}
        />
      </Box>
      {!!errors?.[name] ? (
        <FormHelperText sx={{ marginLeft: 0 }} error={true}>
          {errors?.[name]}
        </FormHelperText>
      ) : null}
      {description ? (
        <ElementDescription>{description}</ElementDescription>
      ) : null}
    </FormControl>
  );
};

export default BaseDateRangePicker;
export { INVALID_DATE_RANGE };
